type WindVaneCall = (
    service: string,
    action: string,
    params: Record<string, unknown>,
    onSuccess: (res: unknown) => void,
    onError: (err: unknown) => void,
) => void

interface WindVaneError {
    ret?: string
    message?: string
}

declare global {
    interface Window {
        WindVane: {
            call: WindVaneCall
        }
    }
}

/** 检查是否在 WindVane 环境中 */
const isWindVaneEnvironment = (): boolean => {
    return (
        typeof window !== "undefined" &&
        window.WindVane &&
        typeof window.WindVane.call === "function"
    )
}

/**
 * 封装 WindVane Promise 版接口
 */
export const useWindVane = () => {
    /** 跳转到 Lazada 内部页面 */
    const push = (url: string): Promise<unknown> => {
        return new Promise((resolve, reject) => {
            if (!isWindVaneEnvironment()) {
                console.warn("WindVane not available, falling back to window.open")
                window.open(url, "_blank")
                resolve("fallback: opened in browser")
                return
            }

            window.WindVane.call(
                "LANavigator",
                "push",
                { url },
                () => {
                    console.log("push success")
                    resolve("push success")
                },
                (e) => {
                    console.error("push error", e)
                    if ((e as WindVaneError)?.ret === "HY_NOT_IN_WINDVANE") {
                        console.log("Falling back to window.open")
                        window.open(url, "_blank")
                        resolve("fallback: opened in browser")
                    } else {
                        reject(e)
                    }
                },
            )
        })
    }

    /** 在外部浏览器中打开页面 */
    const pushInBrowser = (url: string): Promise<unknown> => {
        return new Promise((resolve, reject) => {
            if (!isWindVaneEnvironment()) {
                console.warn("WindVane not available, falling back to window.open")
                window.open(url, "_blank")
                resolve("fallback: opened in browser")
                return
            }

            window.WindVane.call(
                "LANavigator",
                "push",
                { url, method: "browser" },
                () => {
                    console.log("pushInBrowser success")
                    resolve("pushInBrowser success")
                },
                (e) => {
                    console.error("pushInBrowser error", e)
                    if ((e as WindVaneError)?.ret === "HY_NOT_IN_WINDVANE") {
                        console.log("Falling back to window.open")
                        window.open(url, "_blank")
                        resolve("fallback: opened in browser")
                    } else {
                        reject(e)
                    }
                },
            )
        })
    }

    /** 唤起 Lazada 收银台 */
    const tradePay = (tradeNO: string): Promise<unknown> => {
        return new Promise((resolve, reject) => {
            if (!isWindVaneEnvironment()) {
                console.warn("WindVane not available for tradePay")
                reject(new Error("Not in WindVane environment"))
                return
            }

            window.WindVane.call(
                "LazFakeMiniApiPlugin",
                "tradePay",
                { tradeNO },
                (res) => {
                    console.log("tradePay success", res)
                    resolve(res)
                },
                (error) => {
                    console.error("tradePay error", error)
                    reject(error)
                },
            )
        })
    }

    return {
        push,
        pushInBrowser,
        tradePay,
        isWindVaneEnvironment: isWindVaneEnvironment(),
    }
}
